import React, { useState } from "react";
import { useQueries } from "../../context/QueryContext";
import { EMPLOYEE_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import toast from "react-hot-toast";

const CATEGORIES = [
    { value: "technical", label: "Technical" },
    { value: "process", label: "Process" },
    { value: "general", label: "General" },
    { value: "career", label: "Career" },
    { value: "other", label: "Other" },
];


export default function EmployeeQueries() {
    const { myQueries, loading, postQuery, updateQueryStatus, deleteQuery } = useQueries();
    const [showForm, setShowForm] = useState(false);
    const [expandedQuery, setExpandedQuery] = useState(null);
    const [formData, setFormData] = useState({
        title: "",
        question: "",
        category: "general",
    });

    // ── Handle form submit ──────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title.trim() || !formData.question.trim()) {
            toast.error("Please fill in all fields");
            return;
        }

        try {
            await postQuery(formData.title, formData.question, formData.category);
            toast.success("Query posted successfully! Team lead will review it soon.");
            setFormData({ title: "", question: "", category: "general" });
            setShowForm(false);
        } catch (err) {
            toast.error("Failed to post query: " + err.message);
        }
    };

    // ── Handle close query ──────────────────────────────────────
    const handleCloseQuery = async (queryId) => {
        try {
            await updateQueryStatus(queryId, "Resolved");
            toast.success("Query marked as Resolved");
        } catch (err) {
            toast.error("Failed to close query: " + err.message);
        }
    };

    // ── Sort queries: Pending first, then In Progress, then Resolved ─────
    const sortedQueries = [...myQueries].sort((a, b) => {
        const statusOrder = { "Pending": 0, "In Progress": 1, "Resolved": 2 };
        return (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3);
    });

    return (
        <Layout navItems={EMPLOYEE_NAV} panelLabel="My Queries">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Ask Your Team Lead</h1>
                    <p className="page-subtitle">
                        Post your doubts and get answers from your team lead
                    </p>
                </div>
                <button
                    onClick={() => setShowForm(!showForm)}
                    className="btn btn-primary"
                >
                    {showForm ? "Cancel" : "+ Ask a Question"}
                </button>
            </div>

            {/* Query Form */}
            {showForm && (
                <div className="form-card" style={{ marginBottom: "1.5rem" }}>
                    <h3 className="form-card-title">Post Your Query</h3>
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>Title *</label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) =>
                                    setFormData({ ...formData, title: e.target.value })
                                }
                                placeholder="Brief title of your question"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Your Question *</label>
                            <textarea
                                value={formData.question}
                                onChange={(e) =>
                                    setFormData({ ...formData, question: e.target.value })
                                }
                                placeholder="Describe your question or doubt in detail..."
                                rows="5"
                                required
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Category</label>
                                <select
                                    value={formData.category}
                                    onChange={(e) =>
                                        setFormData({ ...formData, category: e.target.value })
                                    }
                                >
                                    {CATEGORIES.map((cat) => (
                                        <option key={cat.value} value={cat.value}>
                                            {cat.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
                            <button type="submit" className="btn-primary">
                                Post Query
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setFormData({ title: "", question: "", category: "general" });
                                    setShowForm(false);
                                }}
                                className="btn-secondary"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Queries List */}
            <div className="dashboard-card">
                <div className="card-header">
                    <h3 className="card-title">All Queries</h3>
                </div>
                {loading ? (
                    <div className="card-loading">Loading queries...</div>
                ) : sortedQueries.length === 0 ? (
                    <div className="card-empty">
                        No queries yet. Post a question to get answers from your team lead!
                    </div>
                ) : (
                    <div className="announcements-full-list">
                        {sortedQueries.map((query) => (
                            <div key={query.id} className={`announcement-card priority-${query.status === "Resolved" ? "low" : query.status === "Pending" ? "high" : "medium"}`}>
                                <div 
                                    className="announcement-card-header" 
                                    style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                                    onClick={() => setExpandedQuery(expandedQuery === query.id ? null : query.id)}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                        <span className="announcement-priority-dot" style={{ marginTop: 0 }} />
                                        <span style={{ fontWeight: 600 }}>{query.title}</span>
                                        <span style={{ fontSize: "0.75rem", background: "#f3f4f6", padding: "0.15rem 0.5rem", borderRadius: "99px", color: "#4b5563" }}>
                                            {query.status?.toUpperCase()}
                                        </span>
                                    </div>
                                    <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>
                                        {expandedQuery === query.id ? "▲" : "▼"}
                                    </span>
                                </div>
                                
                                <p className="announcement-card-msg" style={{ marginTop: "0.5rem", marginBottom: "0.5rem" }}>
                                    {query.question.slice(0, 100)}{query.question.length > 100 ? "..." : ""}
                                </p>

                                <div className="announcement-card-meta">
                                    <span>
                                        {CATEGORIES.find((c) => c.value === query.category)?.label || query.category}
                                    </span>
                                    <span>
                                        {query.createdAt?.toDate ? new Date(query.createdAt.toDate()).toLocaleDateString() : "Recently"}
                                    </span>
                                </div>

                                {/* Expanded View */}
                                {expandedQuery === query.id && (
                                    <div className="ann-read-panel" style={{ marginTop: "1rem", borderTop: "1px solid #e5e7eb", paddingTop: "1rem" }}>
                                        <div style={{ marginBottom: "1.5rem" }}>
                                            <h4 style={{ fontSize: "0.85rem", fontWeight: 700, marginBottom: "0.5rem" }}>Your Question:</h4>
                                            <p style={{ fontSize: "0.8rem", color: "#374151", whiteSpace: "pre-wrap" }}>{query.question}</p>
                                        </div>

                                        {/* Replies */}
                                        {query.replies && query.replies.length > 0 ? (
                                            <div style={{ marginBottom: "1.5rem" }}>
                                                <h4 style={{ fontSize: "0.85rem", fontWeight: 700, marginBottom: "0.5rem" }}>Replies ({query.replies.length}):</h4>
                                                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                                                    {query.replies.map((reply) => (
                                                        <div key={reply.id} style={{ background: "#f8fafc", padding: "0.75rem", borderRadius: "0.375rem", borderLeft: "3px solid #3b82f6" }}>
                                                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.25rem" }}>
                                                                <span style={{ fontWeight: 600, fontSize: "0.8rem", color: "#1e3a8a" }}>{reply.repliedByName}</span>
                                                                <span style={{ fontSize: "0.7rem", color: "#64748b" }}>
                                                                    {reply.createdAt?.toDate ? reply.createdAt.toDate().toLocaleString() : (reply.createdAt instanceof Date ? reply.createdAt.toLocaleString() : "Recently")}
                                                                </span>
                                                            </div>
                                                            <p style={{ fontSize: "0.8rem", color: "#1e293b", margin: 0, whiteSpace: "pre-wrap" }}>{reply.reply}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{ marginBottom: "1.5rem", padding: "0.75rem", background: "#fefce8", border: "1px solid #fef08a", borderRadius: "0.375rem" }}>
                                                <p style={{ margin: 0, fontSize: "0.8rem", color: "#854d0e" }}>⏳ Awaiting response from your team lead...</p>
                                            </div>
                                        )}

                                        {/* Actions */}
                                        <div style={{ display: "flex", gap: "0.5rem", borderTop: "1px solid #e5e7eb", paddingTop: "0.75rem" }}>
                                            {query.status !== "Resolved" && (
                                                <button
                                                    onClick={() => {
                                                        if (window.confirm("Mark this query as resolved? You can still view the responses.")) {
                                                            handleCloseQuery(query.id);
                                                        }
                                                    }}
                                                    className="btn-primary"
                                                    style={{ background: "#10b981" }}
                                                >
                                                    ✓ Mark as Resolved
                                                </button>
                                            )}
                                            <button
                                                onClick={() => {
                                                    if (window.confirm("Delete this query?")) {
                                                        deleteQuery(query.id).then(() => toast.success("Query deleted"));
                                                    }
                                                }}
                                                className="btn-secondary"
                                                style={{ color: "#ef4444", borderColor: "#ef4444", marginLeft: "auto" }}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Layout>
    );
}
